import * as React from 'react';
import { Box, FormControl, ListItemIcon, MenuItem, OutlinedInput, Select, useTheme } from '@mui/material';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import { ThemeProps } from "./Common.interfaces";
import { MeterOption } from './UcMeterSelectV2';
// @ts-ignore
import { uomToCommodity, commodityToUom } from '../../lib/Formatters';
import { isMeterInRange } from 'utils/helpers/general.helpers';
import useAccount from '../../lib/useAccount';
import { USAGE_METER_COMMODITIES } from '../usage/MyUsage.constants';
import ClearIcon from '@mui/icons-material/Clear';
import { TFunction, useTranslation } from "react-i18next";

interface UcMeterSelectProps {
    startDate?: Date;
    endDate?: Date;
    onlyCommodity?: boolean;
    id?: string;
    onChange: Function;
    forcedWidth?: string;
    datacyid?: string;
    disabled?: boolean;
    uom?: string;
    setMeterOptions?: any;
    marginTop?: string;
}

export const UcMeterSelectModular = React.forwardRef((props: UcMeterSelectProps, ref) => {
    const { onlyCommodity } = props;
    const { account } = useAccount();
    const { t } = useTranslation();
    const [open, setOpen] = React.useState(false);
    const theme: ThemeProps = useTheme();

    const [selectedMeterOption, setSelectedMeterOption] = React.useState<any>(null);
    const [meterOptions, setMeterOptions] = React.useState<any>([]);

    const commodityText = (t: TFunction<"translation", undefined>, commodity: string, serialNo: string | undefined, meterNickname: string | undefined) => {
        let displayText = "";
        if (meterNickname !== null && meterNickname !== undefined && meterNickname !== "")
            displayText = meterNickname;
        else if (serialNo !== null && serialNo !== undefined && serialNo !== "")
            displayText = serialNo;

        if (commodity.toUpperCase() === "ELECTRIC") {
            return t("Electric - ") + displayText + (serialNo === null ? "All Meters" : "")
        }
        else {
            return t("Water - ") + displayText + (serialNo === null ? "All Meters" : "")
        }
    }

    const prevMeterOptionsRef = React.useRef<any>([]);

    React.useEffect(() => {
        if (JSON.stringify(prevMeterOptionsRef.current) !== JSON.stringify(meterOptions)) {
            if (props.setMeterOptions) {
                props.setMeterOptions(meterOptions);
            }

            prevMeterOptionsRef.current = meterOptions;
        } 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [meterOptions, props.setMeterOptions]);

    React.useEffect(() => {
        if (account?.metersJson) {
            const commodity = uomToCommodity(props.uom);
            const meters = JSON.parse(account.metersJson);
            let metersAdjusted: any[] = [];

            let electricMeters = meters
                .filter((item: any) => item.meterCommodity === USAGE_METER_COMMODITIES.ELECTRIC && isMeterInRange(item, props?.startDate, props?.endDate));

            if (electricMeters.length > 1 || (onlyCommodity && electricMeters.length > 0)) {
                metersAdjusted = [(
                    {
                        id: "-1",
                        meterCommodity: USAGE_METER_COMMODITIES.ELECTRIC,
                        serialNo: null,
                        uom: "KWH"
                    }
                )];
            }

            if (!onlyCommodity) {
                metersAdjusted = [...metersAdjusted, ...electricMeters];
            }  

            let waterMeters = meters
                .filter((item: any) => item.meterCommodity === USAGE_METER_COMMODITIES.WATER && isMeterInRange(item, props?.startDate, props?.endDate));

            if (waterMeters.length > 1 || (onlyCommodity && waterMeters.length > 0)) {
                metersAdjusted = [...metersAdjusted, (
                    {
                        id: "-2",
                        meterCommodity: USAGE_METER_COMMODITIES.WATER,
                        serialNo: null,
                        uom: "GAL"
                    }
                )]
            }

            if (!onlyCommodity) {
                metersAdjusted = [...metersAdjusted, ...waterMeters];
            }

            const metersAdjustedIds = metersAdjusted.map(meter => meter.id);
            const meterOptionsIds = meterOptions.map((meter: any) => meter.id);

            const arraysHaveSameIds = metersAdjustedIds.every(id => meterOptionsIds.includes(id))
                && meterOptionsIds.every((id: number) => metersAdjustedIds.includes(id));

            const meterExistsInNewOptions = metersAdjustedIds.includes(selectedMeterOption?.id);
           
            if (!arraysHaveSameIds) {
                setMeterOptions(metersAdjusted);

                if (metersAdjusted.length > 0 && !meterExistsInNewOptions) {
                    let option = metersAdjusted.find((x) => x.meterCommodity === commodity);
                    if (!option) { // new meters do not have existing commodity
                        option = metersAdjusted[0];
                    }
                    setSelectedMeterOption(option);
                    props.onChange(option);
                }
            }
        }
    }, [account, props?.startDate, props?.endDate, props.uom, selectedMeterOption, onlyCommodity, meterOptions, props]);

    const handleChange = (event: any) => {
        const { target: { value } } = event;
        let valueOption = meterOptions.find((x: MeterOption) => x.id === value);

        if (valueOption) {
            valueOption.uom = commodityToUom(valueOption.meterCommodity);
            setSelectedMeterOption(valueOption);
            props.onChange(valueOption);
        }
    };

    let id = React.useId();
    
    return (
        <FormControl sx={{ minWidth: 120, pb: 1, width: props.forcedWidth ?? '100%', mt: props.marginTop }} size="small">  
            <Select
                ref={ref}
                id={props.id ?? "ucMeterSelect" + id}
                data-cy-id={props.datacyid}
                open={open}
                value={selectedMeterOption?.id ?? "No options available..."}
                fullWidth={false}
                disabled={props.disabled || meterOptions.length === 0}
                title={selectedMeterOption?.serialNo}
                sx={{ fontSize: 15 }}
                onChange={handleChange}
                onOpen={() => {
                    setOpen(true);
                }}
                onClose={() => {
                    setOpen(false);
                }}
                input={<OutlinedInput />}
                renderValue={() => (
                    (meterOptions.length > 0 && selectedMeterOption !== undefined) ? (
                        selectedMeterOption?.meterCommodity === "ELECTRIC" ? (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <ElectricBoltIcon sx={{ color: (theme.props?.Commodity?.kwh?.main), pr: 1, fontSize: 15 }} />
                                {commodityText(t, "ELECTRIC", selectedMeterOption?.serialNo, selectedMeterOption?.meterNickname)}
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <WaterDropIcon sx={{ color: (theme.props?.Commodity?.gal?.main), pr: 1, fontSize: 15 }} />
                                {commodityText(t, "WATER", selectedMeterOption?.serialNo, selectedMeterOption?.meterNickname)}
                            </Box>
                        )
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <ClearIcon sx={{ pr: 1 }} />
                            {t("No options available...")}
                        </Box>
                    )
                )}
            >
                {meterOptions?.map((option: MeterOption) => (
                    <MenuItem
                        key={option.id}
                        value={option.id}
                        sx={{ fontSize: 15 }}
                    >
                        {option.meterCommodity === "ELECTRIC" ? (
                            <>
                                <ListItemIcon>
                                    <ElectricBoltIcon sx={{ color: (theme.props?.Commodity?.kwh?.main), fontSize: 15 }} />
                                </ListItemIcon>
                                {commodityText(t, "ELECTRIC", option.serialNo, option.meterNickname)}
                            </>
                        ) : (
                            <>
                                <ListItemIcon>
                                    <WaterDropIcon sx={{ color: (theme.props?.Commodity?.gal?.main), fontSize: 15 }} />
                                </ListItemIcon>
                                {commodityText(t, "WATER", option.serialNo, option.meterNickname)}
                            </>
                        )}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
});